import { missingSnowflakeLoadFields, type SnowflakeLoadField } from "./snowflakeLoadFields";

/**
 * #923, #375: the one rule for "this instance's destination may take a run". A destination is
 * Draft until a test passes (owner, #923): never tested, its last test failed, or a tested field
 * changed since (platform `destination_readiness`, the trigger `instance_bindings_destination_draft`).
 * While it is not Ready nothing that writes data runs: the due scan skips the firing
 * (`runner/scheduler/dueScan.ts`), and Run now, a retry and a dbt run are a 409
 * (`server/services/RunService.ts`). The client's banner reads the same answer.
 *
 * Pure, so the client, the server and the runner share it. Only presence is read, never a value:
 * a key is "set or not".
 */

export const DESTINATION_DRAFT_REASONS = [
  "incomplete",
  "never_tested",
  "test_failed",
  "changed",
  // Owner 2026-10-13 (2B): Databricks is not offered yet; every Databricks binding is blocked.
  "databricks_coming_soon",
] as const;
export type DestinationDraftReason = (typeof DESTINATION_DRAFT_REASONS)[number];

export const DESTINATION_READY_SOURCES = ["test", "carried"] as const;
/** `test`: a test on the Destination screen passed. `carried`: Ready when this rule shipped. */
export type DestinationReadySource = (typeof DESTINATION_READY_SOURCES)[number];

export type DestinationReadiness =
  | { state: "not_set" }
  | { state: "draft"; reason: "incomplete"; missing: string[] }
  | { state: "draft"; reason: Exclude<DestinationDraftReason, "incomplete"> }
  | { state: "ready"; source: DestinationReadySource };

/** What the rule reads of an `instance_bindings` row. */
export interface BindingReadinessRow {
  destinationType: "snowflake" | "databricks" | string;
  snowflakeAccount: string | null;
  snowflakeUser: string | null;
  /** Whether a private key is set; never the reference. */
  snowflakePrivateKey: boolean;
  snowflakeDatabase: string | null;
  snowflakeSchema: string | null;
  databricksHost: string | null;
  databricksSpClientId: string | null;
  /** Whether a service principal secret is set. */
  databricksSpSecret: boolean;
  databricksWarehouseId: string | null;
  /** `destination_state`: `draft` or `ready`. */
  destinationState: string;
  /** `destination_test_outcome`: null (never tested), `passed` or `failed`. */
  destinationTestOutcome: string | null;
  /** `destination_test_source`: null, `test` or `carried`. */
  destinationTestSource: string | null;
}

/** The fields a Databricks destination needs before it can run, in the form's order. */
export const DATABRICKS_RUN_FIELDS = [
  "databricksHost",
  "databricksSpClientId",
  "databricksSpSecret",
  "databricksWarehouseId",
] as const;

const blank = (value: string | null | undefined): boolean => !value?.trim();

/**
 * The destination's state. In order:
 * - no row, a Snowflake row whose account, user, key and database are all empty (the schema is
 *   pre-filled), or a Databricks row with no host: `not_set` (#375);
 * - a Snowflake row missing one of the five load fields (#924's rule): Draft `incomplete`, even
 *   when the stored state says Ready;
 * - a Databricks row with a host: Draft `databricks_coming_soon`, whatever is stored (owner
 *   2026-10-13, 2B: Databricks destinations are coming soon, so nothing runs on one);
 * - stored Ready: Ready;
 * - else Draft, the reason from the stored outcome: none is `never_tested`, `failed` is
 *   `test_failed`, and `passed` while Draft is `changed` (a tested field moved since).
 */
export function destinationReadiness(row: BindingReadinessRow | null): DestinationReadiness {
  if (!row) return { state: "not_set" };
  if (row.destinationType === "databricks") {
    if (blank(row.databricksHost)) return { state: "not_set" };
    return { state: "draft", reason: "databricks_coming_soon" };
  }
  if (
    blank(row.snowflakeAccount) &&
    blank(row.snowflakeUser) &&
    !row.snowflakePrivateKey &&
    blank(row.snowflakeDatabase)
  ) {
    return { state: "not_set" };
  }
  const missing: SnowflakeLoadField[] = missingSnowflakeLoadFields(row);
  if (missing.length > 0) return { state: "draft", reason: "incomplete", missing };
  if (row.destinationState === "ready") {
    return { state: "ready", source: row.destinationTestSource === "test" ? "test" : "carried" };
  }
  if (row.destinationTestOutcome === "failed") return { state: "draft", reason: "test_failed" };
  if (row.destinationTestOutcome === "passed") return { state: "draft", reason: "changed" };
  return { state: "draft", reason: "never_tested" };
}

/** True when a run may be created on this destination. */
export function isDestinationRunnable(readiness: DestinationReadiness): boolean {
  return readiness.state === "ready";
}

/** #375: the sentence for an instance with no destination, on the banner and in the 409. */
export const DESTINATION_NOT_SET_MESSAGE =
  "Destination not set: imports and transformations won't run until a destination is added.";

/** Owner 2026-10-13 (2B): the sentence for a Databricks destination, wherever it is blocked. */
export const DATABRICKS_COMING_SOON_MESSAGE =
  "Databricks destinations are coming soon. Use Snowflake for now.";

/**
 * #923: the sentence for a Draft destination, in the 409. #1106: true for every reader. Test needs
 * `instance.binding.write`, a platform admin's alone, and a Contributor has no Settings item.
 */
export const DESTINATION_NOT_READY_MESSAGE =
  "The destination has not passed its test, so nothing runs yet. A platform admin tests it on Settings, Destination.";

/** #923: the banner's sentence for a Draft destination. */
export const DESTINATION_DRAFT_BANNER =
  "Destination is a draft: imports and transformations won't run until it passes its test.";

/** The banner's second line, by reason. `missing` names the empty fields. */
export function draftReasonLine(readiness: DestinationReadiness): string | null {
  if (readiness.state !== "draft") return null;
  switch (readiness.reason) {
    case "incomplete":
      return readiness.missing.length > 0
        ? `Missing: ${readiness.missing.map(readinessFieldName).join(", ")}.`
        : "Some fields are missing.";
    case "never_tested":
      return "It has not been tested yet.";
    case "test_failed":
      return "Its last test failed.";
    case "changed":
      return "It changed since its last test.";
    case "databricks_coming_soon":
      return DATABRICKS_COMING_SOON_MESSAGE;
  }
}

const FIELD_NAMES: Record<string, string> = {
  snowflakeAccount: "account",
  snowflakeUser: "user",
  snowflakePrivateKey: "private key",
  snowflakeDatabase: "database",
  snowflakeSchema: "schema",
};

/** A field id as a sentence names it ("private key"). */
export function readinessFieldName(field: string): string {
  return FIELD_NAMES[field] ?? field;
}

/**
 * The columns a test proves, so a change to one makes a Ready destination Draft again: the
 * platform trigger `instance_bindings_destination_draft` lists the same columns (pinned by
 * `platformDestinationReadiness.migration.test.ts`). The dbt fields, the Teams webhook, the table
 * name case, the skyData columns setting (#2466), the storage integration, the stage name and the storage fields are not tested.
 */
export const DESTINATION_TESTED_COLUMNS = [
  "destination_type",
  "snowflake_account",
  "snowflake_user",
  "snowflake_role",
  "snowflake_warehouse",
  "snowflake_database",
  "snowflake_schema",
  "snowflake_private_key",
  "snowflake_key_passphrase",
] as const;
